/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useMemo, useState } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import parsers from '@sportninja/common/reducers/parsers'
import { connect } from 'react-redux'
import { getPlayerTypesBySportId } from '@sportninja/common/selectors/types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link, useLocation } from 'react-router-dom'
import { Tooltip, Table, Typography, Avatar } from 'antd'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'

import { playerForm } from '../../components/Form/form-configs'
import { InviteAction } from '../../components/ActionPanel/ActionPanelActions'
import OptionButtonEdit from '../../components/List/OptionButtons/Edit'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import TeamRosterBasedList from '../../components/List/TeamRosterBasedList'
import MoreOptions from '../../components/List/OptionButtons/More'
import EditEntitySubTitle from '../EditEntitySubTitle'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import QuestionMarkIcon from '../../components/List/icons/QuestionMarkIcon'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Icon from '../../components/Icon'

dayjs.extend(utc)

const PlayerListTable = ({
  teamId,
  teamName,
  permission = {},
  playerTypes,
  refreshKey,
  onComplete,
  isInFrame,
  shouldShowWaiverStatus = false,
  isSoccer = false,
  sportName = 'hockey',
  sportId,
  lockRosterDate,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const location = useLocation()
  const existingRosterId = useMemo(
    () => location?.state?.rosterId,
    [location?.state]
  )

  const [sort, setSort] = useState('name_first')
  const [direction, setDirection] = useState('asc')

  const handleSort = (field, order) => {
    setSort(field)
    setDirection(order)
  }

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: 'name',
        sorter: true,
        width: isMobile ? 200 : 300,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'name_first'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => {
          let to
          if (item?.id) {
            to = `${ROUTES.PLAYER_ROOT}/${item.id}`
          }
          return (
            <Link
              to={to || '#'}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                max-width: 268px;
                overflow: hidden;
                color: ${colors.NEUTRAL_0};
                &:hover {
                  color: ${colors.PRIMARY};
                }
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item) ||
                  '/images/generic_placeholders/placeholder_player.png'
                }
                icon={<Icon name='user' fontSize={14} />}
                shape='square'
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  padding: 2px;
                  flex-shrink: 0;
                  width: 32px;
                  height: 32px;
                `}
              />
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Typography.Text
                  strong
                  ellipsis
                  css={css`
                    display: block;
                    width: 100%;
                    color: inherit;
                  `}
                >
                  {item?.name_first} {item?.name_last}
                </Typography.Text>
                {item?.customer_identifier && (
                  <Typography.Text
                    type='secondary'
                    ellipsis
                    css={css`
                      font-size: 12px;
                    `}
                  >
                    {item.customer_identifier}
                  </Typography.Text>
                )}
              </div>
            </Link>
          )
        },
      },
      {
        title: '#',
        dataIndex: 'player_number',
        key: 'player_number',
        sorter: true,
        showSorterTooltip: false,
        width: 60,
        sortOrder:
          sort === 'player_number'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
      },
      {
        title: t('common:position'),
        key: 'position',
        sorter: true,
        showSorterTooltip: false,
        width: 120,
        sortOrder:
          sort === 'player_type.id'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) =>
          playerTypes?.find(
            (type) => type.id === parseInt(item?.player_type?.id, 10)
          )?.name_full || '-',
      },
      {
        title: 'DOB',
        key: 'dob',
        width: 120,
        render: (_, item) => {
          const dateAsString = item?.birth_date
            ? dayjs.utc(item.birth_date).format('YYYY-MM-DD')
            : ''
          if (dateAsString === 'Invalid Date') {
            return `${dateAsString} | ${item?.birth_date}`
          } else {
            return dateAsString
          }
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_, item) => {
          const activeSuspensions =
            Array.isArray(item?.suspensions) &&
            item.suspensions.filter((s) => s.is_active) &&
            item.suspensions.length > 0

          if (activeSuspensions) {
            const suspension = item.suspensions.sort((a, b) => {
              const aStartDate = dayjs.utc(a.start_date)
              const bStartDate = dayjs.utc(b.start_date)
              return aStartDate.isBefore(bStartDate)
                ? -1
                : aStartDate.isAfter(bStartDate)
                ? 1
                : 0
            })[0]

            const startDate = suspension?.start_date
              ? dayjs.utc(suspension.start_date).format('MMM D')
              : ''
            const endDate = suspension?.end_date
              ? dayjs.utc(suspension.end_date).format('MMM D')
              : ''

            return (
              <div>
                <div
                  css={css`
                    text-transform: uppercase;
                    color: ${colors.FIERY_RED};
                    font-size: 14px;
                  `}
                >
                  Suspended
                </div>
                <div
                  css={css`
                    margin-top: 2px;
                    font-size: 13px;
                  `}
                >
                  {startDate} -{' '}
                  {suspension?.enforcement?.id !== 3 && endDate !== ''
                    ? endDate
                    : 'Indefinitely'}
                </div>
              </div>
            )
          } else if (item?.is_injured) {
            return 'Injured'
          }
          return null
        },
      },
    ]

    if (permission?.update) {
      cols.push({
        title: 'Email',
        key: 'email',
        width: 250,
        ellipsis: true,
        render: (_, item) => item?.email || '-',
      })
    }

    if (!isInFrame && shouldShowWaiverStatus) {
      cols.push({
        title: 'Signed Waiver',
        key: 'waiver',
        width: 150,
        render: (_, item) => (item?.is_signed_waiver ? 'Yes' : 'No'),
      })
    }

    if (!isInFrame) {
      cols.push({
        title: 'Invite',
        key: 'invite',
        width: 100,
        render: (_, item) => {
          if (item?.is_accepted) {
            return 'Accepted'
          }
          if (item?.is_invited) {
            return 'Invited'
          }

          return ''
        },
      })
    }

    if (permission?.update || permission?.admin) {
      cols.push({
        // title: 'Options',
        title: '',
        key: 'options',
        align: 'right',
        width: 150,
        render: (_, item, index) => {
          // We need the roster object here to construct the slug correctly,
          // but we are inside columns useMemo.
          // We can pass the roster ID via a closure or context if needed,
          // but simpler is to rely on the fact that `item` comes from the data source
          // which we will construct to include necessary context or just use the parent component's scope if possible.
          // However, `roster` is available in the render prop of TeamRosterBasedList.
          // We will handle this by passing the `roster` to the `columns` useMemo or creating columns inside the render prop.
          // For now, let's assume we can get rosterId from `item` if we inject it, or we move columns definition inside.

          // Actually, let's move columns definition inside the render prop of TeamRosterBasedList or pass roster as dependency.
          return null // Placeholder, actual render will be handled in the component body
        },
      })
    }

    return cols
  }, [
    isMobile,
    sort,
    direction,
    playerTypes,
    isInFrame,
    shouldShowWaiverStatus,
    permission,
  ])

  return (
    <TeamRosterBasedList
      teamId={teamId}
      teamName={teamName}
      permission={permission}
      slug='players'
      refreshKey={refreshKey}
      onComplete={onComplete}
      existingRosterIdFromLocationState={existingRosterId}
      lockRosterDate={lockRosterDate}
    >
      {(roster, refresh) => {
        const getOptions = (item) => {
          if (!item) {
            return []
          }
          const slug = `/teams/${teamId}/rosters/${roster.id}/players/${item.id}`

          const onEdit = async (values) => {
            const { imageData, ...form } = values
            const deleteImage = form?.delete_image || false

            if (form?.phone_number) {
              if (
                form.phone_number === '+' ||
                form.phone_number === '' ||
                form.phone_number.length < 9
              ) {
                form.phone_number = null
              }
            }

            const body = JSON.stringify(form)
            await req(slug, { method: 'PUT', body })

            if (imageData) {
              await req(`/teams/${teamId}/players/${item.id}/image`, {
                method: 'POST',
                body: imageData,
              })
            }

            if (deleteImage) {
              await req(`/teams/${teamId}/players/${item.id}/image`, {
                method: 'DELETE',
              })
            }

            await refresh()
          }

          const opts = [
            permission.update && (
              <OptionButtonEdit
                key='edit'
                form={playerForm(
                  parsers.individualPlayer(item),
                  false,
                  false,
                  true,
                  sportName,
                  false,
                  0,
                  sportId
                )}
                onSubmit={onEdit}
                title={t('GameSetup:editPlayer')}
                subTitle={
                  <>
                    <EditEntitySubTitle
                      id={item?.id || ''}
                      entityType={ENTITY_TYPES.player}
                    />
                    {isCanlan && item?.sf_id ? (
                      <EditEntitySubTitle
                        id={item?.sf_id || ''}
                        entityType={ENTITY_TYPES.salesforce}
                      />
                    ) : null}
                  </>
                }
              />
            ),
            permission.update && (
              <InviteAction
                key='invite'
                invite={{
                  is_invited: item.is_invited,
                  is_accepted: item.is_accepted,
                  invitation_id: item.invitation_id,
                }}
                user={{
                  id: item.id,
                  name_first: item.name_first,
                  name_last: item.name_last,
                  player_type_id: item.player_type.id,
                  email: item?.email,
                }}
                slug={slug}
                onComplete={async () => await refresh()}
              />
            ),
            <MoreOptions
              key='more'
              options={[
                permission.update && {
                  key: 'remove',
                  Component: OptionButtonDelete,
                  componentProps: {
                    buttonText: 'remove',
                    slug,
                    itemId: item.id,
                    iconName: 'user-minus',
                    title: `Remove ${item.name_first} ${item.name_last}`,
                    type: t('common:player'),
                    onComplete: refresh,
                  },
                },
              ].filter(Boolean)}
            />,
          ]

          return opts.filter(Boolean)
        }

        const finalColumns = columns.map((col) => {
          if (col.key === 'options') {
            return {
              ...col,
              render: (_, item) => (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    justify-content: flex-end;
                  `}
                >
                  {getOptions(item)}
                </div>
              ),
            }
          }
          return col
        })

        const players = []
        const affiliates = []

        roster?.players?.forEach((p) => {
          const player = {
            ...p,
            isFromTeamRoster: true,
            isEmailRequired: roster?.require_email,
          }
          if (player.is_affiliate) {
            affiliates.push(player)
          } else {
            players.push(player)
          }
        })

        const sorter = (a, b) => {
          if (sort === 'name_last') {
            if (a?.name_last < b?.name_last) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.name_last > b?.name_last) {
              return direction === 'asc' ? 1 : -1
            }
            if (a?.name_first < b?.name_first) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.name_first > b?.name_first) {
              return direction === 'asc' ? 1 : -1
            }
            return 0
          }

          if (sort === 'name_first') {
            if (a?.name_first < b?.name_first) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.name_first > b?.name_first) {
              return direction === 'asc' ? 1 : -1
            }
            if (a?.name_last < b?.name_last) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.name_last > b?.name_last) {
              return direction === 'asc' ? 1 : -1
            }
            return 0
          }

          if (sort === 'player_number') {
            if (a?.player_number < b?.player_number) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.player_number > b?.player_number) {
              return direction === 'asc' ? 1 : -1
            }
          }

          if (sort === 'player_type.id') {
            if (a?.player_type?.id < b?.player_type?.id) {
              return direction === 'asc' ? -1 : 1
            } else if (a?.player_type?.id > b?.player_type?.id) {
              return direction === 'asc' ? 1 : -1
            }
          }

          return 0
        }

        const sortedPlayers = [...players].sort(sorter)
        const sortedAffiliates = [...affiliates].sort(sorter)

        return (
          <div
            css={css`
              width: 100%;
            `}
          >
            {sortedPlayers.length === 0 &&
            sortedAffiliates.length > 0 ? null : (
              <Table
                columns={finalColumns}
                dataSource={sortedPlayers}
                rowKey='id'
                pagination={false}
                size='small'
                scroll={{ x: isMobile || width < 1200 ? 1000 : undefined }}
                css={getTableStyles(isMobile)}
                onChange={(pagination, filters, tableSorter) => {
                  if (tableSorter.order) {
                    const sortKey = tableSorter.columnKey || tableSorter.field
                    const field =
                      sortKey === 'name'
                        ? 'name_first'
                        : sortKey === 'position'
                        ? 'player_type.id'
                        : sortKey
                    handleSort(
                      field,
                      tableSorter.order === 'ascend' ? 'asc' : 'desc'
                    )
                  } else {
                    handleSort('name_first', 'asc')
                  }
                }}
              />
            )}

            {sortedAffiliates.length > 0 && (
              <>
                <div
                  css={css`
                    margin-top: 24px;
                    margin-bottom: 16px;
                    font-weight: 700;
                    color: ${colors.DEFAULT_FLAIR};
                    text-transform: uppercase;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <span>Affiliates</span>
                  <Tooltip
                    title='Affiliates are not full team members and cannot access attendance or team chat.'
                    placement='top'
                  >
                    <span
                      css={css`
                        cursor: help;
                        display: flex;
                        align-items: center;
                        margin-bottom: 2px;
                      `}
                    >
                      <QuestionMarkIcon width={20} height={20} />
                    </span>
                  </Tooltip>
                </div>
                <Table
                  columns={finalColumns}
                  dataSource={sortedAffiliates}
                  rowKey='id'
                  pagination={false}
                  size='small'
                  scroll={{ x: isMobile || width < 1200 ? 1000 : undefined }}
                  css={getTableStyles(isMobile)}
                  onChange={(pagination, filters, tableSorter) => {
                    if (tableSorter.order) {
                      const sortKey = tableSorter.columnKey || tableSorter.field
                      const field =
                        sortKey === 'name'
                          ? 'name_first'
                          : sortKey === 'position'
                          ? 'player_type.id'
                          : sortKey
                      handleSort(
                        field,
                        tableSorter.order === 'ascend' ? 'asc' : 'desc'
                      )
                    } else {
                      handleSort('name_first', 'asc')
                    }
                  }}
                />
              </>
            )}
          </div>
        )
      }}
    </TeamRosterBasedList>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    playerTypes: getPlayerTypesBySportId(state, sportId),
    isInFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(PlayerListTable)
